import { FC, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals } from 'utils/formats';
import { Button } from 'react-bootstrap';
import { useEquityMarket } from '../contexts/EquityMarketContext';
import { mockedReferenceInfo } from '../mocks/mockedData';

type MetricCardProps = {
  icon: string;
  iconVariant?: 'primary' | 'soft';
  label: string;
  value: string | number;
  subtitle: string;
  isHighlighted?: boolean;
};

const MetricCard: FC<MetricCardProps> = ({
  icon,
  iconVariant = 'soft',
  label,
  value,
  subtitle,
  isHighlighted = false,
}) => {
  const highlightClass = isHighlighted ? 'highlighted' : '';
  const iconClass = iconVariant === 'primary' ? 'text-primary' : 'text-soft';

  return (
    <div
      className={`info-ref-item ${highlightClass} p-3 bg-lighter rounded flex-grow-1`}
      style={{ minWidth: '140px' }}
    >
      <div className="d-flex align-items-center mb-1">
        <em className={`icon ni ${icon} ${iconClass} mr-1`} />
        <span className="sub-text">{label}</span>
      </div>
      <div className="lead-text">{value}</div>
      <div className="text-soft small">{subtitle}</div>
    </div>
  );
};

const EquityMarketReferenceInfo: FC = () => {
  const { t } = useTranslate();
  const { data } = useEquityMarket();
  const [isDemoDataVisible, setIsDemoDataVisible] = useState(false);

  const referenceInfo = isDemoDataVisible
    ? mockedReferenceInfo
    : data.referenceInfo;

  const handleToggleDemoData = () => {
    setIsDemoDataVisible(!isDemoDataVisible);
  };

  return (
    <div className="nk-block nk-block-spacing">
      <div className="card card-bordered">
        <div className="card-inner">
          <div className="nk-block-head-sm d-flex align-items-start justify-content-between mb-3">
            <div className="nk-block-head-content">
              <h5 className="nk-block-title mb-0">
                {t('ReferenceInformation')}
              </h5>
              <p className="text-soft mb-0">
                {t('ReferenceInformationDescription')}
              </p>
            </div>
            <Button
              variant="link"
              className="link link-primary btn btn-link p-0"
              onClick={handleToggleDemoData}
            >
              {isDemoDataVisible ? t('HideDemo') : t('ViewDemo')}
            </Button>
          </div>
          <div className="d-flex flex-wrap info-ref-row">
            <MetricCard
              icon="ni-coin"
              iconVariant="primary"
              label={t('LastValuation')}
              value={`${formatCurrencyDecimals(
                referenceInfo.lastValuation
              )}/${t('Share').toLowerCase()}`}
              subtitle={referenceInfo.lastValuationDate}
              isHighlighted
            />
            <MetricCard
              icon="ni-layers-fill"
              label={t('ShareCapital')}
              value={referenceInfo.totalShares.toLocaleString()}
              subtitle={t('Shares').toLowerCase()}
            />
            <MetricCard
              icon="ni-exchange"
              label={t('LastTransactionPrice')}
              value={`${formatCurrencyDecimals(
                referenceInfo.lastTransactionPrice
              )}/${t('Share').toLowerCase()}`}
              subtitle={referenceInfo.lastTransactionDate}
            />
            <MetricCard
              icon="ni-users"
              label={t('ActivePartnersInMarket')}
              value={referenceInfo.activePartnersInMarket}
              subtitle={t('Partners').toLowerCase()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquityMarketReferenceInfo;
